import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Chapter, Project, ProjectVersion, ProjectVersionStatus } from '@simultrain/shared';
import { api } from '../api/client.js';

export function ProjectDetailPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<(Project & { versions: ProjectVersion[] }) | null>(null);
  const [activeVersion, setActiveVersion] = useState<ProjectVersion | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [editingProject, setEditingProject] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [descDraft, setDescDraft] = useState('');
  const [reorderMode, setReorderMode] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingChapterId, setEditingChapterId] = useState<string | null>(null);
  const [chapterTitleDraft, setChapterTitleDraft] = useState('');

  async function reload() {
    if (!projectId) return;
    const p = await api.getProject(projectId);
    setProject(p);
    const v = p.versions[0] ?? null;
    setActiveVersion(v);
    if (v) setChapters(await api.listChapters(v.id));
  }

  useEffect(() => {
    void reload();
  }, [projectId]);

  async function createChapter(e: React.FormEvent) {
    e.preventDefault();
    if (!activeVersion) return;
    await api.createChapter(activeVersion.id, { title });
    setTitle('');
    setCreating(false);
    setChapters(await api.listChapters(activeVersion.id));
  }

  async function changeVersionStatus(status: ProjectVersionStatus) {
    if (!activeVersion) return;
    try {
      const updated = await api.patchVersionStatus(activeVersion.id, status);
      setActiveVersion(updated);
      setProject((p) => (p ? { ...p, versions: p.versions.map((v) => (v.id === updated.id ? updated : v)) } : p));
    } catch (err) {
      setError(String(err));
    }
  }

  function startEditProject() {
    if (!project) return;
    setNameDraft(project.name);
    setDescDraft(project.description);
    setEditingProject(true);
  }

  async function saveProject(e: React.FormEvent) {
    e.preventDefault();
    if (!project) return;
    try {
      const updated = await api.patchProject(project.id, { name: nameDraft, description: descDraft });
      setProject({ ...project, ...updated });
      setEditingProject(false);
    } catch (err) {
      setError(String(err));
    }
  }

  async function deleteProject() {
    if (!project) return;
    if (!window.confirm(t('projects.deleteConfirm', { name: project.name }))) return;
    try {
      await api.deleteProject(project.id);
      nav('/projects');
    } catch (err) {
      setError(String(err));
    }
  }

  function startEditChapter(c: Chapter) {
    setEditingChapterId(c.id);
    setChapterTitleDraft(c.title);
  }

  async function saveChapterTitle(c: Chapter) {
    const next = chapterTitleDraft.trim();
    setEditingChapterId(null);
    if (!next || next === c.title) return;
    try {
      const updated = await api.patchChapter(c.id, { title: next });
      setChapters((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
    } catch (err) {
      setError(String(err));
    }
  }

  async function changeChapterStatus(c: Chapter, status: 'draft' | 'review' | 'published') {
    try {
      const updated = await api.patchChapter(c.id, { status });
      setChapters((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
    } catch (err) {
      setError(String(err));
    }
  }

  async function deleteChapter(c: Chapter) {
    if (!window.confirm(t('chapters.deleteConfirm', { title: c.title }))) return;
    try {
      await api.deleteChapter(c.id);
      setChapters((prev) => prev.filter((x) => x.id !== c.id));
    } catch (err) {
      setError(String(err));
    }
  }

  async function moveChapter(dragId: string, targetId: string) {
    if (!activeVersion || dragId === targetId) return;
    const order = [...chapters];
    const from = order.findIndex((c) => c.id === dragId);
    const to = order.findIndex((c) => c.id === targetId);
    if (from === -1 || to === -1) return;
    const [moved] = order.splice(from, 1);
    order.splice(to, 0, moved!);
    setChapters(order); // optimistic
    try {
      const updated = await api.reorderChapters(activeVersion.id, order.map((c) => c.id));
      setChapters(updated);
    } catch (err) {
      setError(String(err));
      setChapters(await api.listChapters(activeVersion.id));
    }
  }

  if (!project) return <div className="empty">…</div>;

  return (
    <div>
      {error && <div className="error-banner">{error}</div>}

      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16, alignItems: 'flex-start' }}>
        {editingProject ? (
          <form onSubmit={saveProject} className="card" style={{ flex: 1, marginRight: 16 }}>
            <label>{t('projects.name')}</label>
            <input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} required />
            <label style={{ marginTop: 8 }}>{t('projects.description')}</label>
            <textarea value={descDraft} onChange={(e) => setDescDraft(e.target.value)} />
            <div className="row" style={{ marginTop: 12 }}>
              <button type="submit">{t('steps.save')}</button>
              <button type="button" className="secondary" onClick={() => setEditingProject(false)}>
                {t('projects.cancel')}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <h2 style={{ margin: 0 }}>{project.name}</h2>
            <div className="muted">{project.description || '—'}</div>
            <div className="row" style={{ marginTop: 8 }}>
              <button className="secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={startEditProject}>
                ✎ {t('projects.edit')}
              </button>
              <Link to={`/projects/${project.id}/settings`}>
                <button className="secondary" style={{ fontSize: 12, padding: '4px 10px' }}>
                  ⚙ {t('projects.settings')}
                </button>
              </Link>
              <button className="danger" style={{ fontSize: 12, padding: '4px 10px' }} onClick={deleteProject}>
                🗑 {t('projects.delete')}
              </button>
            </div>
          </div>
        )}
        <div className="row">
          <select
            value={activeVersion?.id ?? ''}
            onChange={async (e) => {
              const v = project.versions.find((vv) => vv.id === e.target.value) ?? null;
              setActiveVersion(v);
              if (v) setChapters(await api.listChapters(v.id));
            }}
          >
            {project.versions.map((v) => (
              <option key={v.id} value={v.id}>
                v{v.versionLabel} · {v.status}
              </option>
            ))}
          </select>
          {activeVersion && (
            <select
              value={activeVersion.status}
              title={t('projects.versionStatusLabel')}
              onChange={(e) => void changeVersionStatus(e.target.value as ProjectVersionStatus)}
            >
              <option value="draft">{t('projects.versionStatusDraft')}</option>
              <option value="review">{t('projects.versionStatusReview')}</option>
              <option value="published">{t('projects.versionStatusPublished')}</option>
            </select>
          )}
          <button onClick={() => setCreating((v) => !v)}>+ {t('chapters.create')}</button>
        </div>
      </div>

      {activeVersion && activeVersion.status !== 'published' && (
        <div className="zoom-warning" style={{ marginBottom: 16 }}>
          {t('projects.versionNotPublishedWarning')}
        </div>
      )}

      {creating && (
        <form onSubmit={createChapter} className="card">
          <label>{t('projects.name')}</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} required />
          <button type="submit" style={{ marginTop: 12 }}>
            {t('chapters.create')}
          </button>
        </form>
      )}

      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>{t('chapters.title')}</h3>
        {chapters.length > 1 && (
          <button
            className={reorderMode ? '' : 'secondary'}
            style={{ fontSize: 12, padding: '4px 10px' }}
            onClick={() => {
              setReorderMode((v) => !v);
              setDraggedId(null);
              setDragOverId(null);
            }}
          >
            {reorderMode ? t('steps.reorderDone') : t('steps.reorder')}
          </button>
        )}
      </div>

      {chapters.length === 0 ? (
        <div className="empty">{t('chapters.empty')}</div>
      ) : (
        chapters.map((c) => (
          <div
            key={c.id}
            className={`card ${dragOverId === c.id ? 'drag-over' : ''} ${reorderMode ? 'draggable' : ''}`}
            draggable={reorderMode}
            onDragStart={() => reorderMode && setDraggedId(c.id)}
            onDragOver={(e) => { if (reorderMode) { e.preventDefault(); setDragOverId(c.id); } }}
            onDragLeave={() => setDragOverId((id) => (id === c.id ? null : id))}
            onDrop={(e) => {
              if (!reorderMode || !draggedId) return;
              e.preventDefault();
              setDragOverId(null);
              const dragId = draggedId;
              setDraggedId(null);
              void moveChapter(dragId, c.id);
            }}
            onDragEnd={() => { setDraggedId(null); setDragOverId(null); }}
            onClick={() => { if (!reorderMode && editingChapterId !== c.id) nav(`/chapters/${c.id}`); }}
            style={{ cursor: reorderMode ? 'grab' : 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <div>
              <div className="muted">
                {reorderMode && <span className="drag-handle" style={{ marginRight: 6 }}>⠿</span>}
                {t('chapters.ordinal', { n: c.ordinal + 1 })}
              </div>
              {editingChapterId === c.id ? (
                <div className="row" onClick={(e) => e.stopPropagation()} style={{ margin: '2px 0' }}>
                  <input
                    autoFocus
                    value={chapterTitleDraft}
                    onChange={(e) => setChapterTitleDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); void saveChapterTitle(c); }
                      if (e.key === 'Escape') { e.preventDefault(); setEditingChapterId(null); }
                    }}
                    style={{ maxWidth: 360 }}
                  />
                  <button style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => void saveChapterTitle(c)}>
                    {t('steps.save')}
                  </button>
                  <button
                    className="secondary"
                    style={{ fontSize: 12, padding: '4px 10px' }}
                    onClick={() => setEditingChapterId(null)}
                  >
                    {t('projects.cancel')}
                  </button>
                </div>
              ) : (
                <h2 style={{ margin: '2px 0' }}>
                  {c.title}
                  {c.status !== 'published' && (
                    <span className="pill-warning">
                      {c.status === 'draft' ? t('chapters.statusDraft') : t('chapters.statusReview')}
                    </span>
                  )}
                  {c.draftChapterId && <span className="pill-warning">{t('chapters.draftInProgress')}</span>}
                  {c.shareMode !== 'private' && <span className="pill-warning">🔗 {t('chapters.shareInProgress')}</span>}
                  {c.ttsStatus && c.ttsStatus.missing > 0 && (
                    <span className="pill-warning">
                      🔊 {t('chapters.ttsMissing', { missing: c.ttsStatus.missing, total: c.ttsStatus.total })}
                    </span>
                  )}
                </h2>
              )}
              <div className="muted">{c.description || '—'}</div>
              {c.ttsStatus && c.ttsStatus.total > 0 && (
                <div className="muted" style={{ fontSize: 12 }}>
                  {t('chapters.ttsLastGenerated')}: {c.ttsStatus.lastGeneratedAt ? new Date(c.ttsStatus.lastGeneratedAt).toLocaleString() : t('chapters.ttsNever')}
                  {' · '}
                  {t('chapters.ttsLastTextChange')}: {c.ttsStatus.lastTextChangeAt ? new Date(c.ttsStatus.lastTextChangeAt).toLocaleString() : '—'}
                </div>
              )}
            </div>
            {!reorderMode && (
              <div className="row" style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                <select
                  value={c.status}
                  title={t('chapters.statusLabel')}
                  onChange={(e) => void changeChapterStatus(c, e.target.value as 'draft' | 'review' | 'published')}
                >
                  <option value="draft">{t('chapters.statusDraft')}</option>
                  <option value="review">{t('chapters.statusReview')}</option>
                  <option value="published">{t('chapters.statusPublished')}</option>
                </select>
                <button
                  className="secondary"
                  style={{ fontSize: 12, padding: '4px 10px' }}
                  onClick={(e) => { e.stopPropagation(); startEditChapter(c); }}
                >
                  ✎ {t('chapters.edit')}
                </button>
                <button
                  className="danger"
                  style={{ fontSize: 12, padding: '4px 10px' }}
                  onClick={(e) => { e.stopPropagation(); void deleteChapter(c); }}
                >
                  {t('chapters.delete')}
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
